<template>
  <div class="page-container">
    <!-- 顶部搜索栏 -->
    <div class="top-container">
      <el-form class="power-form" inline :model="loadForm">
        <div class="form-left-items">
          <el-form-item :label="$str.getKey('数据分析/日期')">
            <el-date-picker v-model="loadForm.timer" @change="timeRangeChange" :disabled-date="disabledDate"
              :range-separator="$str.getKey('数据分析/至')" :clearable="false" type="daterange" format="YYYY-MM-DD"
              size="default" class="filter-date-picker"></el-date-picker>
          </el-form-item>
          <el-form-item :label="$str.getKey('数据分析/选择方阵')">
            <el-select v-model="loadForm.currentUnitId" :placeholder="$str.getKey('数据分析/请选择方阵')"
              @change="currentUnitIdChange" size="default" class="filter-select-input">
              <el-option :label="item.label" :value="item.value" v-for="item in loadForm.selectOptions"
                :key="item.value"></el-option>
            </el-select>
          </el-form-item>
          <el-form-item :label="$str.getKey('数据分析/月份')">
            <el-tooltip :content="$str.getKey('数据分析/查看指定月份的最大需量')" placement="top">
              <el-icon class="question-tip">
                <QuestionFilled />
              </el-icon>
            </el-tooltip>
            <el-date-picker v-model="loadForm.mmdMonth" type="month" :placeholder="$str.getKey('数据分析/请选择月份')"
              :clearable="false" @change="demandMonthChange" :disabled-date="disabledDate" size="default"
              class="filter-month-picker"></el-date-picker>
          </el-form-item>
        </div>
      </el-form>
      <el-form-item>
        <div class="filter-actions">
          <v-btn color="#2655f1" rounded="xl" class="action-btn" @click="onSearch">
            <v-icon start icon="mdi-magnify" />{{ $str.getKey('数据分析/查询') }}
          </v-btn>
          <v-btn variant="outlined" color="#2655f1" rounded="xl" class="action-btn" @click="onExport">
            <v-icon start icon="mdi-download" />{{ $str.getKey('数据分析/导出') }}
          </v-btn>
          <v-btn variant="outlined" color="#2655f1" rounded="xl" class="action-btn" @click="onReset">
            <v-icon start icon="mdi-refresh" />{{ $str.getKey('数据分析/重置') }}
          </v-btn>
        </div>
      </el-form-item>
    </div>
    <!-- 图表区域 -->
    <div class="main-container">
      <div class="chart-title">{{ $str.getKey('数据分析/功率曲线') }}</div>
      <div ref="linesChartDom" class="chart-container"></div>
      <!-- 加载状态 -->
      <div v-if="chartLoading" class="chart-status">
        <div class="status-content">
          <span class="loading-text">{{ $str.getKey('数据分析/加载中...') }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, onBeforeUnmount } from "vue";
import * as echarts from 'echarts';
import { ElMessageBox } from "element-plus";
import { QuestionFilled } from '@element-plus/icons-vue';
import dayjs from "dayjs";
import { cloneDeep } from "@/utils"
import { VirtualLoadTracking } from "./virtualLoadTracking.js"
import { useAppStore } from "@/store/modules/app";

const $str = window.$str;
const appStore = useAppStore();
const { stationId } = appStore.useStationState({
  stationIdWatch() {
    stationIdChangeCb(stationId.value);
  }
});
const stationName = computed(() => { return appStore.stationName; })

const originLoadForm = {
  timer: [dayjs(), dayjs()],
  selectOptions: [],
  currentUnitId: ' ',
  mmdMonth: dayjs().subtract(1, 'month').format('YYYY-MM-DD'),
}
const loadForm = reactive(cloneDeep(originLoadForm));

const loadTrackingOptions = {
  startTime: dayjs(loadForm.timer[0]).format("YYYY-MM-DD ") + "00:00:00",
  endTime: dayjs(loadForm.timer[1]).format("YYYY-MM-DD ") + "23:59:59",
  currentUnitId: loadForm.currentUnitId,
  mmdMonth: loadForm.mmdMonth,
}

const isZh = computed(() => {
  return $str.getLanguage() == 'cn';
})
const loadTracking = new VirtualLoadTracking(stationId.value, loadTrackingOptions, isZh);


const linesChartDom = ref(null);
let linesChartObj = null;
const chartLoading = ref(false); // 图表加载状态
const chartEmpty = ref(false); // 图表是否为空数据


onMounted(() => {
  linesChartObj = echarts.init(linesChartDom.value, null, {
    renderer: 'canvas',
    devicePixelRatio: window.devicePixelRatio || 1,
  });
  observeChartResize();
})


function initPageByStation(id) {
  const opfData = cloneDeep(originLoadForm);
  loadForm.timer = opfData.timer;
  loadTracking.updateTimeRange(...loadForm.timer);
  loadForm.currentUnitId = opfData.currentUnitId;
  loadTracking.updateCurrentUnitId(loadForm.currentUnitId);
  loadForm.selectOptions = opfData.selectOptions;
  chartLoading.value = true;
  chartEmpty.value = false;
  loadTracking.updateStationId(id).then(() => {
    updateSelectOptionsForForm();
    const timerId = setInterval(() => {
      if (linesChartObj && timerId) {
        clearInterval(timerId);
        loadTracking.initCharts(linesChartObj).then(() => {
          checkChartData();
        }).catch(() => {
          chartLoading.value = false;
          chartEmpty.value = true;
        });
      }
    }, 100);
  });
}


function stationIdChangeCb(id) {
  initPageByStation(id);
}


function updateSelectOptionsForForm() {
  if (loadTracking.unitList.length > 0) {
    const selectOptions = [
      {
        label: $str.getKey('数据分析/全部'),
        value: ' ',
        name: undefined,
      }
    ];
    loadTracking.unitList.forEach(it => {
      selectOptions.push({
        label: it.unitName,
        value: it.unitId,
        name: it.unitName,
      });
    });
    loadForm.selectOptions = selectOptions;
  } else {
    loadForm.selectOptions = [];
  }
}

function currentUnitIdChange(val) {
  onSearch();
}

function timeRangeChange() {
  if (loadForm.timer && loadForm.timer.length === 2) {
    const start = dayjs(loadForm.timer[0]);
    const end = dayjs(loadForm.timer[1]);
    loadForm.timer = [start, end];
    if (checkDateRange()) {
      onSearch();
    }
  }
}

function demandMonthChange(month) {
  loadTracking.undateMonthForMaxDemandLine(month);
  timeRangeChange();
}

function checkDateRange(range = 1) {
  const st = dayjs(loadForm.timer[0]);
  const et = dayjs(loadForm.timer[1]);
  const limitDay = st.add(range, 'month');
  if (et >= limitDay) {
    window.dispatchNotify({
      type: 'info',
      message: $str.getKey('数据分析/日期范围过大，请选择范围为1个月内的日期')
    })
    return false;
  }
  return true;
}

function disabledDate(date) {
  const latestHalfYear = dayjs().subtract(6, 'month');
  return dayjs(date) > dayjs() || dayjs(date) < latestHalfYear;
}

function onSearch() {
  if (!loadForm.timer || loadForm.timer.length !== 2) {
    window.dispatchNotify({
      type: 'warning',
      message: $str.getKey('数据分析/请选择日期范围')
    });
    return false;
  }

  chartLoading.value = true;
  chartEmpty.value = false;

  loadTracking.updateTimeRange(...loadForm.timer);
  loadTracking.updateCurrentUnitId(loadForm.currentUnitId);

  loadTracking.updateCharts(linesChartObj).then(() => {
    checkChartData();
  }).catch(() => {
    chartLoading.value = false;
    chartEmpty.value = true;
  });
}

function onExport() {
  if (!loadForm.timer || loadForm.timer.length !== 2) {
    window.dispatchNotify({
      type: 'warning',
      message: $str.getKey('数据分析/请先选择日期范围')
    });
    return;
  }

  ElMessageBox.confirm(
    $str.getKey('数据分析/是否确认导出负荷跟踪数据？'),
    $str.getKey('数据分析/提示'),
    {
      confirmButtonText: $str.getKey('数据分析/确定'),
      cancelButtonText: $str.getKey('数据分析/取消'),
      confirmButtonClass: 'custom-confirm-btn',
      cancelButtonClass: 'custom-cancel-btn',
      type: "warning"
    }
  ).then(() => {
    const params = {
      startTime: dayjs(loadForm.timer[0]).format("YYYY-MM-DD ") + "00:00:00",
      endTime: dayjs(loadForm.timer[1]).format("YYYY-MM-DD ") + "23:59:59",
      stationId: stationId.value,
      unitId: loadForm.currentUnitId,
      stationName: stationName.value,
      unitName: loadForm.selectOptions.find(it => it.value == loadForm.currentUnitId)?.name ?? '',
    }
    loadTracking.exportLoadTrackingData(params).then(() => {
      window.dispatchNotify({
        type: 'success',
        message: $str.getKey('数据分析/导出成功')
      })
    });
  }).catch(() => { });
}

function onReset() {
  const opfData = cloneDeep(originLoadForm);
  loadForm.timer = opfData.timer;
  loadTracking.updateTimeRange(...loadForm.timer);
  loadForm.currentUnitId = opfData.currentUnitId;
  loadTracking.updateCurrentUnitId(loadForm.currentUnitId);
  chartLoading.value = true;
  chartEmpty.value = false;
  loadTracking.initCharts(linesChartObj).then(() => {
    checkChartData();
  }).catch(() => {
    chartLoading.value = false;
    chartEmpty.value = true;
  });
}

/** 检查图表数据是否为空 */
function checkChartData() {
  chartLoading.value = false;
  const chartData = loadTracking.chartData;
  // 检查是否有数据
  const hasData =
    (chartData.gccmChartData && chartData.gccmChartData.length > 0) ||
    (chartData.gwmChartData && chartData.gwmChartData.length > 0) ||
    (chartData.ploadChartData && chartData.ploadChartData.length > 0) ||
    (chartData.demandTracksChartData && chartData.demandTracksChartData.length > 0);

  chartEmpty.value = !hasData;

  // 如果无数据，清空图表
  if (!hasData && linesChartObj) {
    linesChartObj.clear();
  }
}

// 容器由 flex 布局撑开，侧边栏折叠等场景不会触发 window 的 resize，
// 只靠 window resize 会让 canvas 被 CSS 拉伸从而文字发虚，这里改用 ResizeObserver 观察容器本身
let resizeObserver = null;
function observeChartResize() {
  if (!linesChartDom.value || typeof ResizeObserver === 'undefined') return;
  resizeObserver = new ResizeObserver(() => {
    linesChartObj?.resize();
  });
  resizeObserver.observe(linesChartDom.value);
}

onBeforeUnmount(() => {
  resizeObserver?.disconnect();
  resizeObserver = null;
  linesChartObj?.dispose();
  linesChartObj = null;
})
</script>

<style lang="scss" scoped>
.page-container {
  font-family: 'HarmonyOS Sans SC', sans-serif;
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  overflow-y: auto;

  .top-container {
    height: auto;
    padding: 0 24px;
    box-sizing: border-box;
    background: rgba(255, 255, 255, 1);
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px;

    .form-left-items {
      display: flex;
      align-items: center;
      gap: 16px;
      flex-wrap: wrap;
    }

    .power-form {
      flex: 1 1 auto;
      min-width: 0;
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 16px;
    }

    .el-form-item {
      margin-bottom: 0;
      margin-right: 0;
      display: flex;
      align-items: center;
      height: 40px;

      :deep(.el-form-item__label) {
        font-size: 14px;
        color: rgba(46, 46, 46, 0.85);
        font-weight: 400;
        padding-right: 12px;
        line-height: 40px;
        height: 40px;
        display: flex;
        align-items: center;
      }

      :deep(.el-form-item__content) {
        line-height: 40px;
        display: flex;
        align-items: center;

        .el-range-editor {
          width: 260px !important;
          min-width: 260px !important;
          max-width: 260px !important;
        }
      }
    }

    .filter-date-picker {

      :deep(.el-input__wrapper) {
        width: 100% !important;
        max-width: 100% !important;
        background: #fff;
        border: 1px solid rgba(192, 207, 218, 0.3);
        border-radius: 20px;
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
        padding: 0 16px;
        min-height: 40px;
        height: 40px;
        transition: border-color 0.2s;

        &.is-focus {
          border-color: #3a68ff;
        }
      }

      :deep(.el-input__inner) {
        font-family: "HarmonyOS Sans SC", sans-serif;
        font-size: 14px;
        color: #2E2E2E;
        height: 40px;
        line-height: 40px;

        &::placeholder {
          color: rgba(46, 46, 46, 0.5);
          font-weight: 400;
          font-family: "HarmonyOS Sans SC", sans-serif;
        }
      }

      :deep(.el-input__suffix) {
        color: rgba(46, 46, 46, 0.5);
      }

      :deep(.el-range-input) {
        font-family: "HarmonyOS Sans SC", sans-serif;
        font-size: 14px;
        color: #2E2E2E;
      }

      :deep(.el-range-separator) {
        font-family: "HarmonyOS Sans SC", sans-serif;
        font-size: 14px;
        color: #2E2E2E;
      }
    }

    .filter-select-input {
      width: 230px !important;
      min-width: 230px !important;
      max-width: 230px !important;

      :deep(.el-input__wrapper) {
        width: 100% !important;
        max-width: 100% !important;
        background: #fff;
        border: 1px solid rgba(192, 207, 218, 0.3);
        border-radius: 20px;
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
        padding: 0 16px;
        min-height: 40px;
        height: 40px;
        transition: border-color 0.2s;

        &.is-focus {
          border-color: #3a68ff;
        }
      }

      :deep(.el-input__inner) {
        font-family: "HarmonyOS Sans SC", sans-serif;
        font-size: 14px;
        color: #2E2E2E;
        height: 40px;
        line-height: 40px;

        &::placeholder {
          color: rgba(46, 46, 46, 0.5);
          font-weight: 400;
          font-family: "HarmonyOS Sans SC", sans-serif;
        }
      }

      :deep(.el-input__suffix) {
        color: rgba(46, 46, 46, 0.5);
      }
    }

    .filter-month-picker {
      width: 230px !important;
      min-width: 230px !important;
      max-width: 230px !important;

      :deep(.el-input__wrapper) {
        width: 100% !important;
        max-width: 100% !important;
        background: #fff;
        border: 1px solid rgba(192, 207, 218, 0.3);
        border-radius: 20px;
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
        padding: 0 16px;
        min-height: 40px;
        height: 40px;
        transition: border-color 0.2s;

        &.is-focus {
          border-color: #3a68ff;
        }
      }

      :deep(.el-input__inner) {
        font-family: "HarmonyOS Sans SC", sans-serif;
        font-size: 14px;
        color: #2E2E2E;
        height: 40px;
        line-height: 40px;

        &::placeholder {
          color: rgba(46, 46, 46, 0.5);
          font-weight: 400;
          font-family: "HarmonyOS Sans SC", sans-serif;
        }
      }

      :deep(.el-input__suffix) {
        color: rgba(46, 46, 46, 0.5);
      }
    }

    .question-tip {
      position: relative;
      left: -8px;
      color: rgba(46, 46, 46, 0.5);
      cursor: pointer;
    }

    .filter-actions {
      display: flex;
      align-items: center;
      gap: 12px;
      flex-shrink: 0;
      height: 40px;
    }
  }

  .main-container {
    flex: 1;
    width: 100%;
    padding: 20px;
    background: rgba(255, 255, 255, 1);
    border-radius: 0 0 20px 20px;
    display: flex;
    flex-direction: column;
    min-height: 0;
    position: relative;

    .chart-title {
      font-family: 'HarmonyOS Sans SC', sans-serif;
      font-size: 16px;
      color: rgba(46, 46, 46, 1);
      font-weight: 500;
      margin-bottom: 0;
    }

    .chart-container {
      flex: 1;
      width: 100%;
      min-height: 0;
    }

    .chart-status {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      background: rgba(255, 255, 255, 0.9);
      z-index: 10;

      .status-content {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 12px;

        .loading-text,
        .empty-text {
          font-family: "HarmonyOS Sans SC", sans-serif;
          font-size: 14px;
          color: rgba(46, 46, 46, 0.65);
          font-weight: 400;
        }
      }
    }
  }

  // 设置图表tooltip的样式
  ::v-deep .chart-tooltip {
    padding: 12px 16px !important;
    border-radius: 8px !important;
    background: #fff !important;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12) !important;
    border: none !important;
  }

  ::v-deep .chart-tooltip__time {
    margin-bottom: 8px;
    font-family: "PingFang SC";
    font-size: 12px;
    font-weight: 400;
    line-height: 20px;
    color: #6A727C;
  }

  ::v-deep .chart-tooltip__items-container {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  ::v-deep .chart-tooltip__series {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    padding: 4px 8px;
    border-radius: 6px;
  }

  ::v-deep .chart-tooltip__series-name,
  ::v-deep .chart-tooltip__value {
    font-family: "PingFang SC";
    font-size: 12px;
    font-weight: 400;
    line-height: 20px;
    margin-left: 4px;
    color: var(--text-icon-font-gy-190-primary, rgba(0, 0, 0, 0.90));
  }

  ::v-deep .chart-tooltip__point {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    margin-right: 4px;
    flex-shrink: 0;
  }
}

.el-date-editor {
  .el-range__close-icon {
    display: none !important;
  }
}

// 选中日期时的颜色
.el-date-editor.el-input__wrapper:hover {
  box-shadow: none !important;
}
</style>
